import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import AthleteContextHeader from '../components/AthleteContextHeader';
import AthleteSearchFilter from '../components/AthleteSearchFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StaffMedical = ({ athleteId: propAthleteId }) => {
  const navigate = useNavigate();
  const isScopedMode = !!propAthleteId;
  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [medicalStatus, setMedicalStatus] = useState(null);
  const [injuries, setInjuries] = useState([]);
  const [recoveryPlans, setRecoveryPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [injuryDialogOpen, setInjuryDialogOpen] = useState(false);
  const [recoveryDialogOpen, setRecoveryDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);

  const [newInjury, setNewInjury] = useState({
    injury_type: '',
    body_part: '',
    start_date: '',
    estimated_return_date: '',
    note: ''
  });

  const [newRecoveryPlan, setNewRecoveryPlan] = useState({
    plan_description: '',
    restrictions: ''
  });

  const [statusUpdate, setStatusUpdate] = useState('healthy');

  useEffect(() => {
    if (propAthleteId) {
      // Athlete-scoped mode: fetch only this athlete
      axios.get(`${API}/athletes/${propAthleteId}`).then(res => {
        setAthletes([res.data]);
        selectAthlete(res.data);
        setLoading(false);
      }).catch(() => { toast.error('Failed to fetch athlete'); setLoading(false); });
    } else {
      fetchMyAthletes();
    }
  }, [propAthleteId]);

  const fetchMyAthletes = async () => {
    try {
      const response = await axios.get(`${API}/athletes`);
      setAthletes(response.data);
    } catch (error) {
      toast.error('Failed to fetch athletes');
    } finally {
      setLoading(false);
    }
  };

  const selectAthlete = async (athlete) => {
    setSelectedAthlete(athlete);
    await fetchAthleteMedicalData(athlete.id);
  };

  const fetchAthleteMedicalData = async (athleteId) => {
    try {
      const [statusRes, injuriesRes, plansRes] = await Promise.all([
        axios.get(`${API}/medical/athlete/${athleteId}`),
        axios.get(`${API}/injuries/athlete/${athleteId}`),
        axios.get(`${API}/recovery-plans/athlete/${athleteId}`)
      ]);
      
      setMedicalStatus(statusRes.data);
      setInjuries(injuriesRes.data);
      setRecoveryPlans(plansRes.data);
    } catch (error) {
      console.error('Failed to fetch medical data:', error);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedAthlete) return;
    
    try {
      if (medicalStatus) {
        await axios.put(`${API}/medical/${medicalStatus.id}`, {
          status: statusUpdate
        });
      } else {
        await axios.post(`${API}/medical`, {
          athlete_id: selectedAthlete.id,
          status: statusUpdate
        });
      }
      toast.success('Medical status updated');
      setStatusDialogOpen(false);
      fetchAthleteMedicalData(selectedAthlete.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleAddInjury = async (e) => {
    e.preventDefault();
    if (!selectedAthlete) return;

    try {
      await axios.post(`${API}/injuries`, {
        athlete_id: selectedAthlete.id,
        ...newInjury
      });
      toast.success('Injury recorded');
      setInjuryDialogOpen(false);
      setNewInjury({ injury_type: '', body_part: '', start_date: '', estimated_return_date: '', note: '' });
      fetchAthleteMedicalData(selectedAthlete.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record injury');
    }
  };

  const handleUpdateInjuryStatus = async (injuryId, newStatus) => {
    try {
      await axios.put(`${API}/injuries/${injuryId}`, {
        status: newStatus
      });
      toast.success('Injury status updated');
      fetchAthleteMedicalData(selectedAthlete.id);
    } catch (error) {
      toast.error('Failed to update injury status');
    }
  };

  const handleAddRecoveryPlan = async (e) => {
    e.preventDefault();
    if (!selectedAthlete) return;

    try {
      await axios.post(`${API}/recovery-plans`, {
        athlete_id: selectedAthlete.id,
        ...newRecoveryPlan
      });
      toast.success('Recovery plan created');
      setRecoveryDialogOpen(false);
      setNewRecoveryPlan({ plan_description: '', restrictions: '' });
      fetchAthleteMedicalData(selectedAthlete.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create recovery plan');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      healthy: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      injured: 'bg-red-100 text-red-800 border-red-200',
      recovering: 'bg-amber-100 text-amber-800 border-amber-200',
      restricted: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return colors[status] || colors.healthy;
  };

  const getInjuryStatusBadge = (status) => {
    const colors = {
      active: 'bg-red-100 text-red-800 border-red-200',
      recovering: 'bg-amber-100 text-amber-800 border-amber-200',
      closed: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700'
    };
    return colors[status] || colors.active;
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <AthleteContextHeader athleteId={propAthleteId} moduleName="Health" />
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Medical & Physical Condition
          </h2>
          <p className="text-gray-600 dark:text-gray-500 mt-1">Track injuries and manage recovery plans</p>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Athlete Selection Sidebar */}
          {!isScopedMode && (
          <div className="col-span-3">
            <Card data-testid="athlete-list">
              <CardHeader>
                <CardTitle className="text-lg">My Athletes</CardTitle>
              </CardHeader>
              <CardContent>
                <AthleteSearchFilter athletes={athletes} compact searchFirst>
                  {(filtered) => (
                    <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                      {filtered.map((athlete) => (
                        <button
                          key={athlete.id}
                          onClick={() => selectAthlete(athlete)}
                          className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-sm ${
                            selectedAthlete?.id === athlete.id
                              ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-300'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                          data-testid={`athlete-${athlete.id}`}
                        >
                          <span>{athlete.full_name}</span>
                          {athlete.squad && <span className="text-[10px] text-gray-400 ml-1">{athlete.squad}</span>}
                        </button>
                      ))}
                      {filtered.length === 0 && <p className="text-xs text-gray-400 text-center py-3">No athletes match</p>}
                    </div>
                  )}
                </AthleteSearchFilter>
              </CardContent>
            </Card>
          </div>
          )}

          {/* Medical Information */}
          <div className={isScopedMode ? "col-span-12" : "col-span-9"}>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
              </div>
            ) : !selectedAthlete ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-gray-500 dark:text-gray-500">Select an athlete to view medical information</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Current Status Card */}
                <Card data-testid="medical-status-card">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle>{selectedAthlete.full_name}</CardTitle>
                        <CardDescription>Current Medical Status</CardDescription>
                      </div>
                      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
                        <DialogTrigger asChild>
                          <Button size="sm" data-testid="update-status-button">
                            Update Status
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Update Medical Status</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <Select value={statusUpdate} onValueChange={setStatusUpdate}>
                              <SelectTrigger data-testid="status-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="healthy">Healthy</SelectItem>
                                <SelectItem value="injured">Injured</SelectItem>
                                <SelectItem value="recovering">Recovering</SelectItem>
                                <SelectItem value="restricted">Restricted</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <DialogFooter>
                            <Button onClick={handleUpdateStatus} data-testid="submit-status">
                              Update Status
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {medicalStatus ? (
                      <Badge className={`${getStatusBadge(medicalStatus.status)} text-lg px-4 py-2`} data-testid="current-status">
                        {medicalStatus.status.charAt(0).toUpperCase() + medicalStatus.status.slice(1)}
                      </Badge>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-500">No status recorded</p>
                    )}
                  </CardContent>
                </Card>

                {/* Tabs for Injuries and Recovery Plans */}
                <Tabs defaultValue="injuries">
                  <TabsList>
                    <TabsTrigger value="injuries" data-testid="injuries-tab">Injuries</TabsTrigger>
                    <TabsTrigger value="recovery" data-testid="recovery-tab">Recovery Plans</TabsTrigger>
                  </TabsList>

                  <TabsContent value="injuries" className="space-y-4">
                    <div className="flex justify-end">
                      <Dialog open={injuryDialogOpen} onOpenChange={setInjuryDialogOpen}>
                        <DialogTrigger asChild>
                          <Button data-testid="add-injury-button">+ Add Injury</Button>
                        </DialogTrigger>
                        <DialogContent data-testid="injury-dialog">
                          <DialogHeader>
                            <DialogTitle>Record Injury</DialogTitle>
                          </DialogHeader>
                          <form onSubmit={handleAddInjury}>
                            <div className="space-y-4 py-4">
                              <div>
                                <Label htmlFor="injury_type">Injury Type</Label>
                                <Input
                                  id="injury_type"
                                  value={newInjury.injury_type}
                                  onChange={(e) => setNewInjury({...newInjury, injury_type: e.target.value})}
                                  placeholder="e.g., Muscle Strain"
                                  required
                                  data-testid="injury-type-input"
                                />
                              </div>
                              <div>
                                <Label htmlFor="body_part">Body Part</Label>
                                <Input
                                  id="body_part"
                                  value={newInjury.body_part}
                                  onChange={(e) => setNewInjury({...newInjury, body_part: e.target.value})}
                                  placeholder="e.g., Hamstring"
                                  required
                                  data-testid="body-part-input"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label htmlFor="start_date">Start Date</Label>
                                  <Input
                                    id="start_date"
                                    type="date"
                                    value={newInjury.start_date}
                                    onChange={(e) => setNewInjury({...newInjury, start_date: e.target.value})}
                                    required
                                    data-testid="start-date-input"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="estimated_return_date">Est. Return Date</Label>
                                  <Input
                                    id="estimated_return_date"
                                    type="date"
                                    value={newInjury.estimated_return_date}
                                    onChange={(e) => setNewInjury({...newInjury, estimated_return_date: e.target.value})}
                                    data-testid="return-date-input"
                                  />
                                </div>
                              </div>
                              <div>
                                <Label htmlFor="note">Notes</Label>
                                <Textarea
                                  id="note"
                                  value={newInjury.note}
                                  onChange={(e) => setNewInjury({...newInjury, note: e.target.value})}
                                  placeholder="Additional details..."
                                  data-testid="note-input"
                                />
                              </div>
                            </div>
                            <DialogFooter>
                              <Button type="submit" data-testid="submit-injury">Record Injury</Button>
                            </DialogFooter>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>

                    {injuries.length === 0 ? (
                      <Card>
                        <CardContent className="py-8 text-center">
                          <p className="text-gray-500 dark:text-gray-500">No injuries recorded</p>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="space-y-3" data-testid="injuries-list">
                        {injuries.map((injury) => (
                          <Card key={injury.id} data-testid={`injury-${injury.id}`}>
                            <CardHeader>
                              <div className="flex justify-between items-start">
                                <div>
                                  <CardTitle className="text-lg">{injury.injury_type}</CardTitle>
                                  <CardDescription>{injury.body_part}</CardDescription>
                                </div>
                                <div className="space-x-2">
                                  <Badge className={getInjuryStatusBadge(injury.status)} data-testid={`injury-status-${injury.id}`}>
                                    {injury.status}
                                  </Badge>
                                  {injury.status !== 'closed' && (
                                    <Select onValueChange={(value) => handleUpdateInjuryStatus(injury.id, value)}>
                                      <SelectTrigger className="w-32" data-testid={`update-injury-${injury.id}`}>
                                        <SelectValue placeholder="Update" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="recovering">Recovering</SelectItem>
                                        <SelectItem value="closed">Closed</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  )}
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent className="space-y-2">
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <p className="text-gray-500 dark:text-gray-500">Start Date</p>
                                  <p className="font-medium">{new Date(injury.start_date).toLocaleDateString()}</p>
                                </div>
                                {injury.estimated_return_date && (
                                  <div>
                                    <p className="text-gray-500 dark:text-gray-500">Est. Return</p>
                                    <p className="font-medium">{new Date(injury.estimated_return_date).toLocaleDateString()}</p>
                                  </div>
                                )}
                              </div>
                              {injury.note && (
                                <div className="pt-2 border-t">
                                  <p className="text-sm text-gray-700 dark:text-gray-300">{injury.note}</p>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="recovery" className="space-y-4">
                    <div className="flex justify-end">
                      <Dialog open={recoveryDialogOpen} onOpenChange={setRecoveryDialogOpen}>
                        <DialogTrigger asChild>
                          <Button data-testid="add-recovery-button">+ Add Recovery Plan</Button>
                        </DialogTrigger>
                        <DialogContent data-testid="recovery-dialog">
                          <DialogHeader>
                            <DialogTitle>Create Recovery Plan</DialogTitle>
                          </DialogHeader>
                          <form onSubmit={handleAddRecoveryPlan}>
                            <div className="space-y-4 py-4">
                              <div>
                                <Label htmlFor="plan_description">Plan Description</Label>
                                <Textarea
                                  id="plan_description"
                                  value={newRecoveryPlan.plan_description}
                                  onChange={(e) => setNewRecoveryPlan({...newRecoveryPlan, plan_description: e.target.value})}
                                  placeholder="Detailed recovery protocol..."
                                  required
                                  data-testid="plan-description-input"
                                />
                              </div>
                              <div>
                                <Label htmlFor="restrictions">Restrictions</Label>
                                <Textarea
                                  id="restrictions"
                                  value={newRecoveryPlan.restrictions}
                                  onChange={(e) => setNewRecoveryPlan({...newRecoveryPlan, restrictions: e.target.value})}
                                  placeholder="Activity restrictions..."
                                  data-testid="restrictions-input"
                                />
                              </div>
                            </div>
                            <DialogFooter>
                              <Button type="submit" data-testid="submit-recovery">Create Plan</Button>
                            </DialogFooter>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>

                    {recoveryPlans.length === 0 ? (
                      <Card>
                        <CardContent className="py-8 text-center">
                          <p className="text-gray-500 dark:text-gray-500">No recovery plans</p>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="space-y-3" data-testid="recovery-list">
                        {recoveryPlans.map((plan) => (
                          <Card key={plan.id} data-testid={`recovery-${plan.id}`}>
                            <CardHeader>
                              <CardTitle className="text-lg">Recovery Plan</CardTitle>
                              <CardDescription>
                                Created {new Date(plan.created_at).toLocaleDateString()}
                              </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                              <div>
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Plan Description</p>
                                <p className="text-sm text-gray-600 dark:text-gray-500">{plan.plan_description}</p>
                              </div>
                              {plan.restrictions && (
                                <div className="bg-amber-50 rounded-lg p-3">
                                  <p className="text-sm font-medium text-amber-900 mb-1">⚠️ Restrictions</p>
                                  <p className="text-sm text-amber-800">{plan.restrictions}</p>
                                </div>
                              )}
                              {plan.progress_notes && (
                                <div className="bg-blue-50 rounded-lg p-3">
                                  <p className="text-sm font-medium text-blue-900 mb-1">📋 Progress Notes</p>
                                  <p className="text-sm text-blue-800">{plan.progress_notes}</p>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StaffMedical;
